import React, { useState, useContext, Fragment, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  CardMedia,
  Box,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { SnackbarContext } from "../constant/SnackbarProvider";
import { isIpad } from "../constant/StaffOnboardState";
import HttpClient from "../api/HttpClient";
import { useLoading } from "../constant/LoadingContext";

const openEye = require("../icons/open_eye.png");
const closeEye = require("../icons/close_eye.png");

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    height: "100vh",
    padding: 0,
    maxWidth: "none",
    "@media (max-width: 1024px)": {
      flexDirection: "column",
      height: "100vh",
      justifyContent: "space-between",
      overflow: "hidden",
    },
  },
  leftContainer: {
    width: "50%",
    backgroundColor: "#F0EFF6",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "24px",
    overflow: "hidden",
    padding: "20px",
    boxSizing: "border-box",
    "@media (max-width: 1024px)": {
      width: "100%",
      borderRadius: "32px 32px 0 0", 
      padding: "6rem",
      flex: "1",
      minHeight: "320px",
      maxHeight: "45%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
  },
  paper: {
    width: "50%",
    borderRadius: "32px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    "@media (max-width: 1024px)": {
      width: "100%",
      flex: "1",
      borderRadius: "0",
    },
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    width: "403px",
    padding: "40px 0",
    "@media (max-width: 1024px)": {
      padding: "2rem 20px",
      width: "100%",
      maxWidth: "403px",
    },
  },
  input: {
    margin: theme.spacing(2, 0),
    width: "100%",
    "& .MuiInput-underline:before": {
      borderBottomColor: "#CED2D6",
      borderBottomWidth: "1px",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#CED2D6",
    },
    "& .MuiInputBase-input": {
      fontFamily: "Noto Sans",
    }
  },
  inputLabel: {
    color: "#84909F",
    fontSize: "20px",
    fontFamily: "Noto Sans",
    fontWeight: 400,
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
    marginBottom: "24px",
  },
  button: {
    margin: theme.spacing(2, 0),
    padding: "9px 126px",
    borderRadius: "12px",
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 600,
    fontFamily: "Noto Sans",
    textTransform: "none",
    height: "60px",
    transition: "background-color 0.3s",
  },
  buttonActive: {
    backgroundColor: "#454C96",
    "&:hover": {
      backgroundColor: "#3a4080",
    },
  },
  buttonDisabled: {
    backgroundColor: "#CED2D6",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#bdc1c4",
    },
  },
  media: {
    width: "559px",
    height: "559px",
    margin: "auto",
    "@media (max-width: 1024px)": {
      width: "400px",
      height: "400px",
      objectFit: "contain",
    },
    "@media (max-width: 768px)": {
      width: "250px",
      height: "250px",
    },
  },
  headerName: {
    color: "#454C96",
    fontSize: "40px",
    fontWeight: 600,
    paddingBottom: "4rem",
    letterSpacing: "0.04em",
    fontFamily: "Noto Sans",
    "@media (max-width: 1024px)": {
      fontSize: "36px",
      letterSpacing: "0.32em",
      paddingBottom: "2.5rem",
    },
  },
  headerNameMobile: {
    display: "none",
    "@media (max-width: 1024px)": {
      display: "block",
    },
  },
  headerNameDesktop: {
    display: "block",
    "@media (max-width: 1024px)": {
      display: "none",
    },
  },
  eyeIcon: {
    cursor: "pointer",
    maxWidth: "30px",
  },
}));

const LoginPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { setLoading } = useLoading();

  const Snackbar = useContext(SnackbarContext);
  const [showPassword, setShowPassword] = useState(false);
  const [staffCode, setStaffCode] = useState("");
  const [password, setPassword] = useState("");

  const HandleLogin = () => {
    setLoading(true);
    HttpClient().HandleLoginApi(
      { staffCode, password },
      () => {
        setLoading(false);
        Snackbar.open({
          alertProps: { severity: "success" },
          message: "登入成功",
        });
        navigate("/main", { replace: true });
      },
      (error) => {
        setLoading(false);
        Snackbar.open({
          alertProps: { severity: "error" },
          message: "登入失敗 :" + error,
        });
      }
    );
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && staffCode !== "" && password !== "") {
        HandleLogin();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [staffCode, password]);

  const isFormValid = staffCode !== "" && password !== "";
  const buttonClassName = `${classes.button} ${isFormValid ? classes.buttonActive : classes.buttonDisabled}`;

  return (
    <Fragment>
      <Container className={classes.container}>
        <Box className={classes.leftContainer}>
          <Typography variant="h4" className={`${classes.headerName} ${classes.headerNameMobile}`}>
            員工文件系統
            {!!process.env.REACT_APP_SUB_TITLE &&
              `(${process.env.REACT_APP_SUB_TITLE})`}
          </Typography>
          <CardMedia
            component="img"
            image="/assets/login-illustration.svg"
            alt=""
            className={classes.media}
          />
        </Box>
        <Paper className={classes.paper}>
          <Box className={classes.formContainer}>
            <Typography variant="h4" className={`${classes.headerName} ${classes.headerNameDesktop}`}>
              員工文件系統
              {!!process.env.REACT_APP_SUB_TITLE &&
                `(${process.env.REACT_APP_SUB_TITLE})`}
            </Typography>
            <TextField
              label="員工編號"
              value={staffCode}
              onChange={(e) => setStaffCode(e.target.value)}
              className={classes.input}
              InputLabelProps={{
                className: classes.inputLabel,
              }}
            />
            <Box className={classes.passwordContainer}>
              <TextField
                label="密碼"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={classes.input}
                InputLabelProps={{
                  className: classes.inputLabel,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CardMedia
                        onClick={() => setShowPassword(!showPassword)}
                        component="img"
                        image={showPassword ? openEye : closeEye}
                        alt=""
                        className={classes.eyeIcon}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Button
              variant="contained"
              disabled={!isFormValid}
              onClick={() => {
                HandleLogin();
              }}
              className={buttonClassName}
            >
              登入
            </Button>
          </Box>
        </Paper>
      </Container>
    </Fragment>
  );
};

export default LoginPage;
